import { throttle } from 'lodash';
import debounce from 'lodash.debounce';

import store from './index';

import { getEventUsersById, getEventUsersBymmid } from '@/services/event.service';
import loggerService from '@/services/logger.service';
import { makeUnique } from '@/services/utils';
import { getFormattedChatMessage } from '@/store/modules/avroom/actions';
import { makeUsers } from '@/utils/userTransform';

export const resetSidbarSubTab = debounce(
  () => {
    loggerService.info('Resetting chat subtab');
    store.commit('sidebar/setStageChatCurrentTabIndex', 0);
  },
  100,
  {
    maxWait: 100,
    leading: false
  }
);

// Processor for new chat messages

export function makePostThrottle(delay: number = 100) {
  let postsToAdd: any[] = [];
  const runPostThrottleUpdate = throttle(
    () => {
      addPost();
    },
    delay,
    { leading: false }
  );
  function addPost() {
    const postsToBeAdded = postsToAdd;
    postsToAdd = [];
    const postsByChannel: Record<string, any> = {};
    for (let i = 0; i < postsToBeAdded.length; i++) {
      const post = postsToBeAdded[i];
      const channel_id = post.channel_id;
      postsByChannel[channel_id] = postsByChannel[channel_id] || [];
      postsByChannel[channel_id].push(post);
    }
    for (const channel_id in postsByChannel) {
      const chat = (store.state.event.avroom?.chats ?? []).find(chat => chat?.channel?.id == channel_id);
      let previousPost: any = null;
      if (chat?.channel?.messages) {
        previousPost = (chat?.channel?.messages ?? []).slice(-1)[0];
      }
      const formattedMessages = postsByChannel[channel_id].map((post: any) =>
        getFormattedChatMessage(post, previousPost)
      );

      const updateUnread = true;
      store.commit('event/avroom/addMessages', {
        channel_id: channel_id,
        posts: formattedMessages,
        updateUnread
      });
    }
  }
  function schedulePostAdd(post: any) {
    postsToAdd.push(post);
    runPostThrottleUpdate();
  }

  return {
    schedulePostAdd,
    runPostThrottleUpdate
  };
}

// Processor for new reactions
let reactionChanges: Array<{
  type: 'add' | 'remove';
  msg: any;
}> = [];

const runReactionChanges = debounce(
  () => {
    const reactionsToChange = reactionChanges;
    reactionChanges = [];
    reactionsToChange.forEach(item => {
      if (item.msg?.data?.reaction && item.msg?.broadcast?.channel_id) {
        const reaction = JSON.parse(item.msg.data.reaction);
        const channelID = item.msg.broadcast.channel_id;
        if (item.type === 'add') {
          store.dispatch('event/avroom/newReactionReceived', {
            reaction: {
              ...reaction,
              serverResponseState: 'success'
            },
            channelID
          });
        } else if (item.type === 'remove') {
          store.dispatch('event/avroom/reactionRemoved', {
            reaction: {
              ...reaction,
              serverResponseState: 'success'
            },
            channelID
          });
        }
      }
    });
  },
  5000,
  {
    maxWait: 5000,
    leading: true
  }
);

export function scheduleReactionChange(type: 'add' | 'remove', msg: any) {
  reactionChanges.push({
    type,
    msg
  });
  runReactionChanges();
}

// Processor for user addition
const userAdds: Array<{ eventid: string; mmid: string }> = [];

export function scheduleUserAdd(eventid: string, mmid: string) {
  userAdds.push({ eventid, mmid });
}

// Processor for refreshes by userid
let scheduledRefreshes: Array<{ mmid: string; force: boolean }> = [];
const blackListed = new Set();
let runningRefresh: Promise<any> | null = null;

export function getUsersByMmidAndAddtoStore(eventid: string, mmids: string[]) {
  return getEventUsersBymmid(eventid, mmids).then(res => {
    return store.dispatch('event/recieveUsersInBulk', res.data as any, { root: true }).then(() => {
      return makeUsers(res.data as any);
    });
  });
}

const runRefresh = debounce(
  () => {
    if (!runningRefresh) {
      runningRefresh = Promise.resolve();
    }
    /** call api to get users by mmid */
    // Here we do not check for users that might have been downloaded in the previous call.
    // This is because this function is used to do hard refresh in apigateway socket for user updates.
    // This is in contrast to the getuserbyuuid function below.
    runningRefresh = runningRefresh.then(() => {
      return new Promise(resolve => {
        let mmidstocallfor = scheduledRefreshes
          .filter(it => !blackListed.has(it.mmid))
          .filter(it => {
            // If force is true then refresh otherwise refresh if user is not in store
            if (!it.force) {
              const user = store.getters['event/usersbyMmid'][it.mmid];
              if (!user || user.eventUser?.event_role === undefined) {
                return true;
              }
              return false;
            }
            return true;
          })
          .map(it => it.mmid);
        mmidstocallfor = Array.from(new Set(mmidstocallfor));
        scheduledRefreshes.length = 0;
        if (mmidstocallfor.length > 0) {
          const eventid = store.state.route.params.eventID.toString();
          getUsersByMmidAndAddtoStore(eventid, mmidstocallfor)
            .then(users => {
              const receivedMMids = new Set(users.map((user: any) => user.mmid));
              for (const mmid of mmidstocallfor) {
                if (!receivedMMids.has(mmid)) {
                  blackListed.add(mmid);
                  // console.warn(
                  //   `MMID blacklisted to prevent infinite loop! : ${mmid}`
                  // );
                }
              }
              store.dispatch('event/avroom/remakeAvroomStateWithUsers', {
                root: true
              });
              resolve(scheduledRefreshes);
            })
            .catch(err => resolve(err));
        } else {
          loggerService.info('Did not run fmmid refresh', mmidstocallfor);
          resolve(mmidstocallfor);
        }
      });
    });
  },
  5000,
  {
    leading: true,
    maxWait: 5000
  }
);

export function scheduleRefresh(mmid: string, force?: boolean) {
  if (blackListed.has(mmid)) {
    return;
  }

  scheduledRefreshes.push({ mmid, force: force || false });
  runRefresh();
}

// Processor for refreshes by userid
let scheduledUserFetches: Array<{ uuid: string; force: boolean }> = [];

const runScheduledUserFetch = debounce(
  () => {
    /** call api to get users by uuid */
    const uuidstocallfor = scheduledUserFetches
      .filter(it => {
        if (!it?.uuid) {
          return false;
        }
        const user = store.state.event && store.state.event.event.users && store.state.event.event.users[it.uuid];
        if (!user || user.eventUser?.event_role === undefined || it.force) {
          return true;
        }
        return false;
      })
      .map(it => it.uuid);
    const uniqueUuidsTocallFor = makeUnique(uuidstocallfor);
    scheduledRefreshes = [];
    if (uniqueUuidsTocallFor.length > 0) {
      const eventid = store.state.route.params.eventID?.toString();
      getEventUsersById(eventid, uniqueUuidsTocallFor).then(res => {
        const receivedUids = (res?.data as any)?.map((user: any) => user?.id);
        scheduledUserFetches = scheduledUserFetches.filter(it => !receivedUids.includes(it.uuid));
        store.dispatch('event/recieveUsersInBulk', res?.data as any, { root: true });
      });
    }
  },
  5000,
  {
    leading: true,
    maxWait: 5000
  }
);

export function scheduleUserFetch(uuid: string, force?: boolean) {
  if (blackListed.has(uuid)) {
    return;
  }
  scheduledUserFetches.push({ uuid, force: force || false });
  runScheduledUserFetch();
}
